import * as React from 'react';
import { toast } from 'react-toastify';
import { Action } from 'redux';

import { AppState } from 'src';

import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { CreditCardUpdateModel, SavedCreditCard } from '@models/Account';
import { OptionsViewModel } from '@models/common.models';
import { accountService } from '@services/AccountService';
import { NotFoundError } from '@services/BaseService';

import { ContractState } from 'src/contract/contract.reducer';

import { Dispatch } from 'react-redux';
import { AppDispatcher } from '../../../_models/common.models';
import { contractService } from '../../../_services/ContractService';
import { requestAction } from '../../../common/components/common.actions';
import { SimpleAccountViewModel } from '@models/SimpleAccountViewModel';

export enum ContractPaymentActionType {
  GetAffiliatedAccounts = 'GetAffiliatedAccounts',
  GetSavedCreditCardsForContractFailure = 'GetSavedCreditCardsForContractFailure',
  GetSavedCreditCardsForContractStart = 'GetSavedCreditCardsForContractStart',
  GetSavedCreditCardsForContractSuccess = 'GetSavedCreditCardsForContractSuccess',
  SetNewCreditCard = 'SetNewCreditCard',
  SetBillableEntities = 'SetBillableEntities'
}

interface ContractPaymentAction extends Action {
  type: ContractPaymentActionType;
}

export interface SetBillableEntitiesAction extends Action {
  readonly type: ContractPaymentActionType.SetBillableEntities;
  readonly entities: SimpleAccountViewModel[];
}

export interface GetAffiliatedAccountsAction extends ContractPaymentAction {
  affiliatedAccounts: OptionsViewModel[];
}

export interface GetSavedCreditCardsForContractFailureAction extends ContractPaymentAction {}
export interface GetSavedCreditCardsForContractStartAction extends ContractPaymentAction {}
export interface GetSavedCreditCardsForContractSuccessAction extends ContractPaymentAction {
  readonly savedCreditCards: SavedCreditCard[];
}
export interface SetNewCreditCardAction extends ContractPaymentAction {
  readonly creditCard: CreditCardUpdateModel;
  readonly isBalancePayment: boolean;
}

export function loadBillablePartiesForContract(contractId: number) {
  return async (dispatch: AppDispatcher) => {
    return dispatch(
      requestAction(() => contractService.getBillableEntities(contractId), setBillableEntities, {
        overrideFailMessage: 'Unable to load billable entities for contract: ' + contractId
      })
    );
  };
}

export function setBillableEntities(parties: SimpleAccountViewModel[]): SetBillableEntitiesAction {
  return {
    type: ContractPaymentActionType.SetBillableEntities,
    entities: parties
  };
}

export function getAffiliatedAccounts(contractId?: number) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    if (contractId) {
      return dispatch(loadBillablePartiesForContract(contractId));
    }
    const accountIds = gatherEntities(getStore().contract);

    const affiliatedAccounts: OptionsViewModel[] = [];
    for (const accountIdString of Object.keys(accountIds)) {
      affiliatedAccounts.push({ id: +accountIdString, value: accountIds[accountIdString] });
    }

    return dispatch({
      type: ContractPaymentActionType.GetAffiliatedAccounts,
      affiliatedAccounts: affiliatedAccounts
    });
  };
}

export function getSavedCreditCardsForContract() {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    dispatch(getSavedCreditCardsForContractStart());

    try {
      // gather account ids
      const contractState: ContractState = getStore().contract;
      const accountIds = gatherEntities(contractState);

      // gather stored credit cards
      const promises: Promise<SavedCreditCard | undefined>[] = [];
      for (const accountIdString of Object.keys(accountIds)) {
        const promise: Promise<SavedCreditCard | undefined> = accountService
          .getAccount(+accountIdString)
          .then(a => {
            return a && Object.keys(a).length > 0
              ? ({
                  account: a,
                  name: accountIds[accountIdString]
                } as SavedCreditCard)
              : undefined;
          })
          .catch(e => {
            if (e instanceof NotFoundError) {
              return undefined;
            }
            throw new Error(e.message);
          });
        promises.push(promise);
      }
      await Promise.all(promises);
      const savedCreditCards: SavedCreditCard[] = [];
      for (const promise of promises) {
        await promise.then(a => {
          if (a && a.account) {
            savedCreditCards.push(a);
          }
        });
      }

      return dispatch(getSavedCreditCardsForContractSuccess(savedCreditCards));
    } catch (e) {
      return dispatch(getSavedCreditCardsForContractFailure(e.message));
    }
  };
}

export function getSavedCreditCardsForContractFailure(error: string): GetSavedCreditCardsForContractFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error} />, ToastOptions.Error);
  return {
    type: ContractPaymentActionType.GetSavedCreditCardsForContractFailure
  };
}

export function getSavedCreditCardsForContractStart(): GetSavedCreditCardsForContractStartAction {
  return {
    type: ContractPaymentActionType.GetSavedCreditCardsForContractStart
  };
}

export function getSavedCreditCardsForContractSuccess(
  savedCreditCards: SavedCreditCard[]
): GetSavedCreditCardsForContractSuccessAction {
  return {
    type: ContractPaymentActionType.GetSavedCreditCardsForContractSuccess,
    savedCreditCards: savedCreditCards
  };
}

export function setNewCreditCard(creditCard: CreditCardUpdateModel, isBalancePayment: boolean): SetNewCreditCardAction {
  return {
    type: ContractPaymentActionType.SetNewCreditCard,
    creditCard: creditCard,
    isBalancePayment: isBalancePayment
  };
}

// reusable logic
function gatherEntities(contractState: ContractState): object {
  const accountIds = {};
  if (contractState) {
    // customers
    if (contractState.buyerCustomer && contractState.buyerCustomer.accountId) {
      accountIds[contractState.buyerCustomer.accountId!] = `Buyer: ${contractState.buyerCustomer.name || ''}`;
    }
    if (contractState.sellerCustomer && contractState.sellerCustomer.accountId) {
      accountIds[contractState.sellerCustomer.accountId!] = `Seller: ${contractState.sellerCustomer.name || ''}`;
    }

    // RE agents
    if (contractState.coopAgent && contractState.coopAgent.accountId) {
      accountIds[contractState.coopAgent.accountId!] = `Coop Agent: ${contractState.coopAgent.name || ''}`;
    }
    if (contractState.initAgent && contractState.initAgent.accountId) {
      accountIds[contractState.initAgent.accountId!] = `Init Agent: ${contractState.initAgent.name || ''}`;
    }
    if (contractState.additionalRealEstateAgents && contractState.additionalRealEstateAgents.length > 0) {
      for (const reAgent of contractState.additionalRealEstateAgents) {
        if (reAgent && reAgent.accountId) {
          accountIds[reAgent.accountId!] = `Other Agent: ${reAgent.name || ''}`;
        }
      }
    }
  }
  return accountIds;
}



// WEBPACK FOOTER //
// ./src/contract/_components/contract-payment/contract-payment.actions.tsx